import styled from 'styled-components';
import { Operator } from '@ardoq/api-types';
import { space } from '@ardoq/design-tokens';
import { BottomRowWrapper } from './atoms';
import { AsyncSelect, Select } from '@ardoq/select';
import {
  GroupBox,
  InputsWrapper,
  RowHeader,
  RuleWarningContainer,
} from '../../../atoms';
import { TagFormattingRule } from '@ardoq/data-model';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { WarningNotification } from '@ardoq/status-ui';
import {
  NEW_DEFAULT_OPERATOR_TO_LABEL,
  RuleNotificationType,
} from '@ardoq/query-builder';
import {
  buildColorWarningKey,
  ValueWarningMessage,
  FormattingColorPicker,
  perspectivesRowDragAndDropHandlers,
  useFormattingContext,
  ColorPickerWrapper,
} from '@ardoq/perspectives';
import { FormattingRuleHeader } from './FormattingRuleHeader';
import { isRuleVersionSupported } from '@ardoq/perspectives';
import { type I18nLocale, t } from '@ardoq/i18n';

const TagFormattingRuleRowContainer = styled(BottomRowWrapper)`
  gap: ${space.small};
`;

type FormattingRuleRowByTagProperties = {
  formattingRule: TagFormattingRule;
  colorPaletteAlignment: 'left' | 'right';
  i18nLocale?: I18nLocale;
};
export const FormattingRuleRowByTag = ({
  formattingRule: rule,
  colorPaletteAlignment = 'right',
  i18nLocale = {},
}: FormattingRuleRowByTagProperties) => {
  const formattingContext = useFormattingContext();
  const {
    updateRule,
    asyncSuggestionsLoaders,
    tagsFormattingGroups,
    getRuleNotificationParams,
    ruleInfo,
  } = formattingContext;
  const { __id: ruleId, isCollapsed, isInverted } = rule;
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(ruleId || null, useDragAndDropContext());

  const { colorWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarning = colorWarnings[buildColorWarningKey(rule.color)];

  const ruleNotificationParams = getRuleNotificationParams?.(rule);
  const warningMessage =
    ruleNotificationParams?.notificationType === RuleNotificationType.WARNING
      ? ruleNotificationParams.message
      : null;

  const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

  return (
    <GroupBox
      role="group"
      aria-label={t(
        i18nLocale,
        'perspectivesSidebar:formattingRules.conditionalFormattingRuleAriaLabel',
        'Conditional formatting rule'
      )}
      data-testid="group-box-formatting-rule-by-tag"
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      flexDirection="column"
    >
      <FormattingRuleHeader
        rule={rule}
        context={formattingContext}
        i18nLocale={i18nLocale}
      />
      {warningMessage && (
        <RuleWarningContainer data-testid="group-box-formatting-warning-notification">
          <WarningNotification>{warningMessage}</WarningNotification>
        </RuleWarningContainer>
      )}
      {isCollapsed ? null : (
        <>
          <InputsWrapper $isDisabled={isRuleUnsupported}>
            <RowHeader>
              {t(
                i18nLocale,
                'perspectivesSidebar:formattingRules.condition',
                'Condition'
              )}
            </RowHeader>
            <Select
              value={isInverted ? Operator.NOT_EQUAL : Operator.EQUAL}
              options={[
                {
                  value: Operator.EQUAL,
                  label: NEW_DEFAULT_OPERATOR_TO_LABEL[Operator.EQUAL],
                },
                {
                  value: Operator.NOT_EQUAL,
                  label: NEW_DEFAULT_OPERATOR_TO_LABEL[Operator.NOT_EQUAL],
                },
              ]}
              onChange={selectedOption => {
                if (!selectedOption) {
                  return;
                }
                const { value } = selectedOption;
                const isInverted = value === Operator.NOT_EQUAL;
                updateRule(ruleId, { isInverted });
              }}
              menuPortalTarget={null}
            />
            <RowHeader>
              {t(
                i18nLocale,
                'perspectivesSidebar:formattingRules.value',
                'Value'
              )}
            </RowHeader>
            <TagFormattingRuleRowContainer>
              <ColorPickerWrapper $hasWarning={Boolean(colorWarning)}>
                <FormattingColorPicker
                  dataTestId="tag-formatting-rule-color-picker"
                  value={rule.color}
                  onValueChange={color => updateRule(ruleId, { color })}
                  isNewConditionalFormatting={true}
                  colorPaletteAlignment={colorPaletteAlignment}
                />
              </ColorPickerWrapper>
              <AsyncSelect
                dataTestId="tag-formatting-rule-select"
                loadOptions={asyncSuggestionsLoaders.loadTagSuggestionsAsync}
                defaultOptions={tagsFormattingGroups}
                placeholder={t(
                  i18nLocale,
                  'perspectivesSidebar:formattingRules.searchAndSelectTag',
                  'Search and select a tag'
                )}
                value={
                  rule.value ? { value: rule.value, label: rule.value } : null
                }
                onChange={selectedOption => {
                  if (!selectedOption) return;
                  const { value } = selectedOption;
                  updateRule(ruleId, { value });
                }}
                hasWarning={Boolean(colorWarning)}
                menuPortalTarget={null}
              />
            </TagFormattingRuleRowContainer>
            <ValueWarningMessage message={colorWarning} />
          </InputsWrapper>
        </>
      )}
    </GroupBox>
  );
};
